<script setup lang="ts">
import { computed, inject } from 'vue';
import { Plus, X, ArrowUp } from 'lucide-vue-next';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Textarea from '@/components/ui/TextArea.vue';
import type { JsonSchemaField } from '@/types/PromptField';

interface Props {
  field: JsonSchemaField;
  parentField?: JsonSchemaField;
  depth: number;
  disabled?: boolean;
}

interface Emits {
  (e: 'add-child', fieldId: string): void;
  (e: 'remove-field', fieldId: string, parentField?: JsonSchemaField): void;
  (e: 'update-field', fieldId: string, updates: Partial<JsonSchemaField>, parentField?: JsonSchemaField): void;
  (e: 'promote-field', fieldId: string, parentField: JsonSchemaField): void;
  (e: 'toggle-expansion', fieldId: string): void;
}

// Required for recursive components
defineOptions({
  name: 'FieldItem',
});

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Inject parent component's methods and state
const fieldBuilderContext = inject('fieldBuilderContext') as {
  fieldTypeOptions: any;
  getFieldPath: (fieldId: string) => string;
  isFieldNameDuplicatedInLevel: (fieldName: string, siblings: JsonSchemaField[]) => boolean;
  getChildrenOf: (parentId: string) => JsonSchemaField[];
  getChildrenOfFiltered: (parentId: string) => JsonSchemaField[];
  getActualSiblings: (fieldId: string) => JsonSchemaField[];
};

const marginLeft = computed(() => `${props.depth * 20}px`);

// Border colors for different nesting levels
const borderColor = computed(() => {
  const colors = [
    'tw:border-slate-200', // Level 0 (root) - default gray
    'tw:border-blue-300', // Level 1 - blue border
    'tw:border-green-300', // Level 2 - green border
    'tw:border-yellow-400', // Level 3 - yellow border
    'tw:border-purple-300', // Level 4 - purple border
    'tw:border-pink-300', // Level 5+ - pink border
  ];

  const colorIndex = Math.min(props.depth, colors.length - 1);
  return colors[colorIndex];
});
</script>

<template>
  <div
    :style="{ marginLeft }"
    :class="['tw:flex tw:items-start tw:gap-3 tw:rounded tw:border tw:bg-white tw:p-3', borderColor]">
    <div class="tw:flex-1 tw:space-y-2">
      <!-- Field Header -->
      <div class="tw:flex tw:items-center tw:gap-2">
        <!-- Field Path Display -->
        <div class="tw:text-xs tw:text-slate-500">
          {{ fieldBuilderContext.getFieldPath(field.id) }}
        </div>

        <!-- Action Buttons -->
        <div class="tw:ml-auto tw:flex tw:gap-1">
          <button
            @click="emit('add-child', field.id)"
            :disabled="disabled"
            class="tw:flex tw:items-center tw:gap-1 tw:rounded tw:border tw:border-blue-200 tw:bg-blue-50 tw:px-2 tw:py-1 tw:text-xs tw:text-blue-600 tw:hover:bg-blue-100"
            :title="field.type === 'object' ? 'Add child field' : 'Add child field (will convert to object)'">
            <Plus class="tw:h-3 tw:w-3" />
            Add Child
          </button>
          <button
            v-if="parentField"
            @click="emit('promote-field', field.id, parentField)"
            :disabled="disabled"
            class="tw:flex tw:items-center tw:gap-1 tw:rounded tw:border tw:border-purple-200 tw:bg-purple-50 tw:px-2 tw:py-1 tw:text-xs tw:text-purple-600 tw:hover:bg-purple-100"
            title="Move up one level">
            <ArrowUp class="tw:h-3 tw:w-3" />
            Promote
          </button>
          <button
            @click="emit('remove-field', field.id, parentField)"
            :disabled="disabled"
            class="tw:flex tw:items-center tw:gap-1 tw:rounded tw:border tw:border-red-200 tw:bg-red-50 tw:px-2 tw:py-1 tw:text-xs tw:text-red-600 tw:hover:bg-red-100"
            title="Remove field">
            <X class="tw:h-3 tw:w-3" />
            Remove
          </button>
        </div>
      </div>

      <!-- Field Configuration -->
      <div class="tw:grid tw:grid-cols-2 tw:gap-2">
        <div class="tw:space-y-1">
          <Input
            :model-value="field.name"
            @update:model-value="(value) => emit('update-field', field.id, { name: value }, parentField)"
            placeholder="Field name"
            :disabled="disabled"
            :class="
              fieldBuilderContext.isFieldNameDuplicatedInLevel(field.name, fieldBuilderContext.getActualSiblings(field.id))
                ? 'tw:border-red-500 tw:ring-red-500'
                : ''
            "
            label="Name"
            size="sm" />
          <p
            v-if="fieldBuilderContext.isFieldNameDuplicatedInLevel(field.name, fieldBuilderContext.getActualSiblings(field.id))"
            class="tw:text-xs tw:text-red-600">
            Duplicate field name
          </p>
        </div>
        <UISelect
          :model-value="field.type"
          @update:model-value="
            (value) => emit('update-field', field.id, { type: value as 'string' | 'number' | 'boolean' | 'object' | 'array' }, parentField)
          "
          :options="fieldBuilderContext.fieldTypeOptions"
          :disabled="disabled"
          placeholder="Select type..." />
      </div>

      <Textarea
        :model-value="field.description"
        @update:model-value="(value) => emit('update-field', field.id, { description: value }, parentField)"
        placeholder="Field description"
        :disabled="disabled"
        class="tw:min-h-[60px] tw:text-sm"
        label="Description" />

      <div class="tw:flex tw:items-center tw:gap-4">
        <label class="tw:flex tw:items-center tw:gap-2 tw:text-sm">
          <input
            :checked="field.required"
            @change="(e) => emit('update-field', field.id, { required: (e.target as HTMLInputElement).checked }, parentField)"
            type="checkbox"
            :disabled="disabled"
            class="tw:rounded tw:border-slate-300" />
          Required
        </label>
        <div
          v-if="field.sourceField"
          class="tw:text-xs tw:text-blue-600">
          From: {{ field.sourceField.displayName }}
        </div>
      </div>
    </div>
  </div>

  <!-- RECURSIVE PART: Render children (always shown) -->
  <!-- Use getChildrenOfFiltered to automatically unwrap placeholder objects -->
  <template v-if="fieldBuilderContext.getChildrenOfFiltered(field.id).length > 0">
    <FieldItem
      v-for="child in fieldBuilderContext.getChildrenOfFiltered(field.id)"
      :key="child.id"
      :field="child"
      :parent-field="field"
      :depth="depth + 1"
      :disabled="disabled"
      @add-child="(fieldId) => emit('add-child', fieldId)"
      @remove-field="(fieldId, parentField) => emit('remove-field', fieldId, parentField)"
      @update-field="(fieldId, updates, parentField) => emit('update-field', fieldId, updates, parentField)"
      @promote-field="(fieldId, parentField) => emit('promote-field', fieldId, parentField)"
      @toggle-expansion="(fieldId) => emit('toggle-expansion', fieldId)" />
  </template>
</template>
